/**
 * @author Hiroki Moto
 * @description https://marvelapp.com/43fgc52/screen/54179034
 */

import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import equals from 'ramda/src/equals';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import propEq from 'ramda/src/propEq';
import queryString from 'query-string';
import Button from '@components/@core/AnimatedButton';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Filter from '@components/@core/Filter';
import Pagination from '@components/@core/Pagination';
import Search from '@components/LibarySearch';
import Tab from '@components/@core/Tab';
import Status from '@components/LibraryStatus';
import * as CareerCard from '@components/LibraryProgramsCareer';
import * as CertificationCard from '@components/LibraryProgramsCertification';
import { AppActions, DevelopActions } from '~/actions';
import { ProgramTypes, LibraryProgramTabs } from '~/services/config';
import withNavigate from '~/hocs/withNavigate';

class ProgramList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            active: queryString.parse(props.location?.search)?.active || props.match?.params?.activeTab || 'careers',
            filter: null,
            page: 1,
            perPage: 25,
        };
    }

    componentDidMount() {
        const { filter, active, page, perPage } = this.state;
        this.props.getTemplates(active, { search: filter, page, perPage });
        this.props.getQuotaOptions();
    }

    componentDidUpdate(prevProps) {
        if (!equals(this.props.params?.activeTab, prevProps.params?.activeTab)) {
            this.setState({ active: this.props.params?.activeTab });
        }
    }

    handleModal = (e) => {
        this.props.toggleModal(e);
    };

    handlePage = (page) => {
        const { active, filter, perPage } = this.state;
        this.props.getTemplates(active, { search: filter, page, perPage });
        this.setState({ page });
    };

    handlePer = (perPage) => {
        const { active, filter, page } = this.state;
        this.props.getTemplates(active, { search: filter, page, perPage });
        this.setState({ perPage });
    };

    handleSearch = (e) => {
        const filter = e.target.value;
        const { active, page, perPage } = this.state;
        this.props.getTemplates(active, { search: filter, page, perPage });
        this.setState({ filter: e.target.value });
    };

    handleTab = (e) => {
        this.props.getTemplates(e.name, { page: 1, perPage: this.state.perPage });
        this.props.navigate({ search: `?active=${e.name}` });
        this.setState({ active: e.name, page: 1, filter: '' });
    };

    handleOpenDetail = (id) => {
        const { active, filter, page } = this.state;
        const program = find(propEq('id', id), this.props.templates[active].data);
        this.props.getProgramDetail(program, active, {
            pathname: `/library/programs/${active}/${id}/view`,
            state: { filter, page },
        });
    };

    handleDeleteProgram = (template) => {
        const { active, filter, page, perPage } = this.state;
        this.props.deleteTemplate('program', 'delete', template.id, null, null, {
            type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST',
            target: active,
            payload: { search: filter, page, perPage },
        });
    };

    handleChangeMenu = (event, program) => {
        const { active } = this.state;
        const { companyId } = this.props;
        const disabled = filter((x) => x !== active, ProgramTypes);

        switch (event) {
            case 'assign': {
                const payload = {
                    type: 'Assign Program',
                    data: {
                        before: { modules: [program], disabled, levels: program.data.levels, companyId },
                        after: [],
                    },
                };
                this.props.toggleModal(payload);
                break;
            }
            case 'detail view': {
                this.props.getProgramDetail(program, active, {
                    pathname: `/library/programs/${active}/${program.id}/view`,
                    state: { filter: this.state.filter, page: this.state.page },
                });
                break;
            }
            case 'edit': {
                this.props.getProgramDetail(program, active, {
                    pathname: `/library/programs/${active}/${program.id}/edit`,
                    state: { filter: this.state.filter, page: this.state.page },
                });
                break;
            }
            case 'quick assign': {
                this.props.toggleModal({
                    type: 'Quick Assign',
                    data: {
                        before: {
                            template: program,
                            type: active,
                            from: 'template',
                            after: null,
                            companyId,
                        },
                    },
                    callBack: {
                        onDelete: () => this.handleDeleteProgram(program),
                    },
                });
                break;
            }
            case 'delete':
                this.props.toggleModal({
                    type: 'Confirm',
                    data: {
                        before: {
                            title: 'Delete',
                            body: 'Are you sure you want to delete this item?',
                        },
                    },
                    callBack: {
                        onYes: () => this.handleDeleteProgram(program),
                    },
                });
                break;
            case 'duplicate':
                this.props.toggleModal({
                    type: 'Duplicate Module',
                    data: { before: { type: active, object: program } },
                });
                break;
            default:
                break;
        }
    };

    handleAdd = () => {
        this.props.navigate(`/library/programs/${this.state.active}/new`);
    };

    render() {
        const { active } = this.state;
        const { userRole, templates } = this.props;

        return (
            <ErrorBoundary className="dev-library-programs">
                <Filter add={'Health Plan'} onAdd={this.handleAdd} />
                <div className="card">
                    <div className="d-h-between ai-start mb-4 pb-1">
                        <span className="dsl-b22 bold">{active === 'careers' ? 'Health Plan' : 'Certification'}</span>
                        <Search type={active} onChange={this.handleSearch} />
                    </div>
                    <Tab eventKey="careers" active={active}>
                        <Status type="Health Plans" showToggle counts={templates.careers.total} />
                        <CareerCard.List role={userRole} data={templates.careers.data} onModal={this.handleModal} onToggle={this.handleOpenDetail} onChange={this.handleChangeMenu} />
                    </Tab>
                    <Tab eventKey="certifications" active={active}>
                        <Status type="Certifications" showToggle counts={templates.certifications.total} />
                        <CertificationCard.List role={userRole} data={templates.certifications.data} onModal={this.handleModal} onToggle={this.handleOpenDetail} onChange={this.handleChangeMenu} />
                    </Tab>
                    {/* comment out the badge tab for now because it is an upcoming feature */}
                    {/* <Tab eventKey="badges" active={active}>
            <Status type="Badges" showToggle counts={templates.badges.total} />
            <BadgeCard.List
              role={userRole}
              data={templates.badges.data}
              onModal={this.handleModal}
              onToggle={id => this.props.navigate(`/library/programs/badges/${id}/view`)}
            />
          </Tab> */}
                    <Pagination
                        per={Number(templates[active].per_page)}
                        current={templates[active].current_page}
                        total={templates[active].last_page}
                        onChange={this.handlePage}
                        onPer={this.handlePer}
                    />
                    <div className="d-flex mt-2 jc-end">
                        <Button type="high" onClick={this.handleAdd}>
                            Add {active === 'careers' ? 'Health Plan' : 'Certification'}
                        </Button>
                    </div>
                </div>
            </ErrorBoundary>
        );
    }
}

ProgramList.propTypes = {
    userRole: PropTypes.number,
    templates: PropTypes.any,
    getTemplates: PropTypes.func,
    deleteTemplate: PropTypes.func,
    toggleModal: PropTypes.func,
};

ProgramList.defaultProps = {
    userRole: 1,
    templates: {},
    getTemplates: () => {},
    deleteTemplate: () => {},
    toggleModal: () => {},
};

const mapStateToProps = (state) => ({
    userRole: state.app.app_role_id,
    templates: state.develop.templates,
    companyId: state.app.company_info.id,
});

const mapDispatchToProps = (dispatch) => ({
    getTemplates: (target, payload) => dispatch(DevelopActions.getlibrarytemplatessearchRequest(target, payload)),
    deleteTemplate: (target, event, id, data, route, after) => dispatch(DevelopActions.libraryeventRequest(target, event, id, data, route, after)),
    getProgramDetail: (payload, type, route) => dispatch(DevelopActions.libraryprogramdetailRequest(payload, type, route)),
    getQuotaOptions: () => dispatch(DevelopActions.getquotaoptionsRequest()),
    toggleModal: (e) => dispatch(AppActions.modalRequest(e)),
});

const ProgramListWithNavigate = withNavigate(ProgramList);

export default connect(mapStateToProps, mapDispatchToProps)(ProgramListWithNavigate);
